import { useEffect, useRef, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { Icon } from './icons';
import { IconButton } from './buttons';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> { count?: number; variant?: 'unread' | 'mention' | 'dot'; muted?: boolean; label?: string }
export function Badge({ count, variant = 'unread', muted, label, className, children, ...props }: BadgeProps) {
  if (count !== undefined && count <= 0 && variant === 'unread') return null;
  return <span {...props} aria-label={label} className={clsx('hylo-badge', `hylo-badge--${variant}`, muted && 'is-muted', className)}>
    {variant === 'dot' ? null : children ?? (variant === 'mention' ? '@' : count !== undefined && count > 999 ? '999+' : count)}
  </span>;
}

export interface ChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'onClick'> {
  children: ReactNode; icon?: string; selected?: boolean; onClick?: () => void; disabled?: boolean; onRemove?: () => void; removeLabel?: string;
}
export function Chip({ children, icon, selected, onClick, onRemove, removeLabel, disabled, className, ...props }: ChipProps) {
  const content = <>{icon && <Icon name={icon} />}{children}</>;
  return <span {...props} className={clsx('hylo-chip', selected && 'is-selected', className)}>
    {onClick ? <button type="button" disabled={disabled} onClick={onClick} aria-pressed={selected}>{content}</button> : <span className="hylo-chip__label">{content}</span>}
    {onRemove && <IconButton name="xmark" label={removeLabel ?? (typeof children === 'string' ? children : '×')} disabled={disabled} onClick={onRemove} />}
  </span>;
}

export interface SpinnerProps extends HTMLAttributes<HTMLSpanElement> { label?: string; size?: number }
export function Spinner({ label, size, className, style, ...props }: SpinnerProps) {
  return <span {...props} role={label ? 'status' : undefined} aria-label={label} aria-hidden={!label || undefined}
    className={clsx('hylo-spinner', className)} style={{ width: size, height: size, ...style }} />;
}

export interface ProgressProps { value?: number; label: string; className?: string }
const progress = (value?: number) => value === undefined || !Number.isFinite(value) ? undefined : Math.max(0, Math.min(1, value));
export function ProgressBar({ value, label, className }: ProgressProps) {
  const amount = progress(value);
  return <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={amount === undefined ? undefined : Math.round(amount * 100)}
    className={clsx('hylo-progress', amount === undefined && 'is-indeterminate', className)}>
    <span style={{ width: amount === undefined ? undefined : `${amount * 100}%` }} />
  </div>;
}
export interface ProgressRingProps extends ProgressProps { size?: number; strokeWidth?: number }
export function ProgressRing({ value, label, className, size = 36, strokeWidth = 3 }: ProgressRingProps) {
  const amount = progress(value); const radius = (size - strokeWidth) / 2; const length = 2 * Math.PI * radius;
  return <svg role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={amount === undefined ? undefined : Math.round(amount * 100)}
    width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={clsx('hylo-progress-ring', amount === undefined && 'is-indeterminate', className)}>
    <circle className="hylo-progress-ring__track" cx={size / 2} cy={size / 2} r={radius} strokeWidth={strokeWidth} />
    <circle cx={size / 2} cy={size / 2} r={radius} strokeWidth={strokeWidth} strokeDasharray={length}
      strokeDashoffset={length * (1 - (amount ?? .25))} />
  </svg>;
}

export interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> { width?: CSSProperties['width']; height?: CSSProperties['height']; circle?: boolean }
export function Skeleton({ width, height, circle, className, style, ...props }: SkeletonProps) {
  return <span {...props} aria-hidden="true" className={clsx('hylo-skeleton', circle && 'hylo-skeleton--circle', className)} style={{ width, height, ...style }} />;
}

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> { icon?: string; title: string; message?: ReactNode; action?: ReactNode }
export function EmptyState({ icon = 'tray', title, message, action, className, ...props }: EmptyStateProps) {
  return <div {...props} className={clsx('hylo-empty-state', className)}><div className="hylo-empty-state__disc"><Icon name={icon} size={34} /></div>
    <h2>{title}</h2>{message && <p>{message}</p>}{action}</div>;
}
export function ErrorState({ icon = 'exclamationmark.triangle', className, ...props }: EmptyStateProps) {
  return <EmptyState {...props} icon={icon} role="alert" className={clsx('hylo-error-state', className)} />;
}

export interface BannerProps extends HTMLAttributes<HTMLDivElement> {
  title?: string; children?: ReactNode; icon?: string; tone?: 'info' | 'success' | 'warning' | 'error';
  action?: ReactNode; onDismiss?: () => void; dismissLabel?: string;
}
export function Banner({ title, children, icon, tone = 'info', action, onDismiss, dismissLabel, className, ...props }: BannerProps) {
  return <div role={tone === 'error' ? 'alert' : 'status'} {...props} className={clsx('hylo-banner', 'hylo-glass', `hylo-banner--${tone}`, className)}>
    <Icon name={icon ?? ({ info: 'info.circle', success: 'checkmark.circle', warning: 'exclamationmark.circle', error: 'exclamationmark.triangle' }[tone])} />
    <div className="hylo-banner__content">{title && <strong>{title}</strong>}{children}</div>{action}
    {onDismiss && <IconButton name="xmark" label={dismissLabel ?? '×'} onClick={onDismiss} />}
  </div>;
}

export interface ToastProps extends BannerProps { open: boolean; duration?: number; onDismiss: () => void }
export function Toast({ open, duration = 4000, onDismiss, className, ...props }: ToastProps) {
  const callback = useRef(onDismiss);
  useEffect(() => { callback.current = onDismiss; }, [onDismiss]);
  useEffect(() => {
    if (!open || duration <= 0) return;
    const timer = window.setTimeout(() => callback.current(), duration);
    return () => window.clearTimeout(timer);
  }, [open, duration]);
  return open ? <Banner {...props} onDismiss={onDismiss} className={clsx('hylo-toast', className)} /> : null;
}
